// Minimal query hook for the sphinx REST API. Deliberately not TanStack Query
// (not a dep here): a keyed fetch with a module-level cache, a global
// invalidation counter that every mounted query re-fetches on, and optional
// polling. Semantics mirror Convex's `useQuery`: `undefined` while loading or
// skipped (key === null), `null` for a 404, data otherwise.
import { useCallback, useEffect, useRef, useState } from "react";
import { SphinxApiError } from "./sphinx-api";

type Listener = () => void;

const listeners = new Set<Listener>();
const cache = new Map<string, unknown>();

/** Re-fetch every mounted query (call after any mutation). */
export function invalidateSphinxQueries(): void {
  for (const listener of listeners) listener();
}

/** Drop cached data (tests / sign-out style resets). */
export function clearSphinxQueryCache(): void {
  cache.clear();
}

export type SphinxQueryState<T> = {
  data: T | null | undefined;
  error: Error | null;
  isLoading: boolean;
  refetch: () => void;
};

export type SphinxQueryOptions<T> = {
  /**
   * Polling interval in ms, or a function of the latest data returning the
   * interval (or null/0 to stop). Used by live-run views to track progress
   * while a run/iteration is pending/running.
   */
  pollMs?: number | ((data: T) => number | null | undefined);
};

export function useSphinxQueryState<T>(
  key: string | null,
  fetcher: () => Promise<T>,
  options: SphinxQueryOptions<T> = {}
): SphinxQueryState<T> {
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const pollRef = useRef(options.pollMs);
  pollRef.current = options.pollMs;

  const [state, setState] = useState<{
    key: string | null;
    data: T | null | undefined;
    error: Error | null;
  }>(() => ({
    key,
    data: key !== null && cache.has(key) ? (cache.get(key) as T) : undefined,
    error: null,
  }));
  const [tick, setTick] = useState(0);
  const refetch = useCallback(() => setTick((t) => t + 1), []);

  // Global invalidation → refetch.
  useEffect(() => {
    listeners.add(refetch);
    return () => {
      listeners.delete(refetch);
    };
  }, [refetch]);

  useEffect(() => {
    if (key === null) {
      setState({ key, data: undefined, error: null });
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const run = async () => {
      try {
        const data = await fetcherRef.current();
        if (cancelled) return;
        cache.set(key, data);
        setState({ key, data, error: null });
        const poll = pollRef.current;
        const ms = typeof poll === "function" ? poll(data) : poll;
        if (ms && ms > 0) timer = setTimeout(run, ms);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof SphinxApiError && err.status === 404) {
          setState({ key, data: null, error: null });
          return;
        }
        const error = err instanceof Error ? err : new Error(String(err));
        console.error(`[sphinx-api] ${key}`, error);
        setState((prev) => ({ key, data: prev.key === key ? prev.data : undefined, error }));
        // Retry errors on the poll cadence (if any) so a transient DB outage
        // recovers without a manual refresh.
        const poll = pollRef.current;
        const ms = typeof poll === "function" ? null : poll;
        if (ms && ms > 0) timer = setTimeout(run, ms);
      }
    };
    void run();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [key, tick]);

  const current = state.key === key ? state : { key, data: undefined, error: null };
  return {
    data: key === null ? undefined : current.data,
    error: current.error,
    isLoading: key !== null && current.data === undefined && !current.error,
    refetch,
  };
}

/** Convex-`useQuery`-shaped: `undefined` loading/skipped, `null` not found. */
export function useSphinxQuery<T>(
  key: string | null,
  fetcher: () => Promise<T>,
  options: SphinxQueryOptions<T> = {}
): T | null | undefined {
  return useSphinxQueryState(key, fetcher, options).data;
}

/** Wrap a mutation so every mounted query refreshes after it settles. */
export function withInvalidate<A extends unknown[], R>(
  fn: (...args: A) => Promise<R>
): (...args: A) => Promise<R> {
  return async (...args: A) => {
    try {
      return await fn(...args);
    } finally {
      invalidateSphinxQueries();
    }
  };
}
